import { Check } from 'lucide-react';

import { cn } from '../../lib/utils';

export interface StepperStep {
  label: string;
  description?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  activeStep: number;
  className?: string;
}

/**
 * Minimal horizontal step indicator, replacing MUI's Stepper/Step/StepLabel.
 * No shadcn/ui equivalent ships upstream, so this is a small custom primitive
 * built from the same design tokens as the rest of packages/ui.
 */
function Stepper({ steps, activeStep, className }: StepperProps) {
  return (
    <ol className={cn('flex w-full flex-col gap-4 sm:flex-row sm:items-start', className)}>
      {steps.map((step, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;
        return (
          <li key={step.label} className="flex flex-1 items-start gap-3 sm:flex-col sm:items-center sm:text-center">
            <span
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                isCompleted && 'border-primary bg-primary text-primary-foreground',
                isActive && !isCompleted && 'border-primary text-primary',
                !isActive && !isCompleted && 'border-border text-muted-foreground',
              )}
            >
              {isCompleted ? <Check className="h-3.5 w-3.5" /> : index + 1}
            </span>
            <div className="flex flex-col sm:items-center">
              <span
                className={cn(
                  'text-sm font-medium',
                  isActive || isCompleted ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
              {step.description ? (
                <span className="text-xs text-muted-foreground">{step.description}</span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export { Stepper };
